import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Conversation, ConversationMember, ConversationMemberWithProfile, ConversationSummary, Profile } from '../types';
import { useAuth } from '../hooks/useAuth';
import { usePresence } from '../hooks/usePresence';
import { getSupabase } from '../lib/supabase';
import AdminDashboard from './AdminDashboard';
import ChatWindow from './ChatWindow';
import ConversationList from './ConversationList';
import FriendRequests from './FriendRequests';
import GroupChatModal from './GroupChatModal';
import ProfilePanel from './ProfilePanel';
import SettingsModal, { type RelayPreferences } from './SettingsModal';
import SidebarRail, { type RailPanel } from './SidebarRail';
import { showBrowserNotification, useNotificationPermission } from '../hooks/useNotificationPermission';
import UserProfileModal from './UserProfileModal';

const preferencesStorageKey = 'relay-preferences-v1';
const defaultPreferences: RelayPreferences = {
  theme: 'midnight',
  compactMode: false,
  glassPanels: true,
  reduceMotion: false,
};

function loadPreferences(): RelayPreferences {
  try {
    const stored = window.localStorage.getItem(preferencesStorageKey);
    return stored ? { ...defaultPreferences, ...JSON.parse(stored) } : defaultPreferences;
  } catch {
    return defaultPreferences;
  }
}

export default function AppLayout() {
  const { user, profile, signOut, updateProfile } = useAuth();
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<RailPanel>('chat');
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [preferences, setPreferences] = useState<RelayPreferences>(loadPreferences);
  const { permission: notificationPermission, requestPermission } = useNotificationPermission();
  const onlineIds = usePresence(user, profile);

  const selectedConversation = useMemo(
    () => conversations.find((conversation) => conversation.id === selectedConversationId) ?? null,
    [conversations, selectedConversationId],
  );

  const loadConversations = useCallback(async () => {
    if (!user) return;

    const client = getSupabase();
    setLoadingConversations(true);

    const { data: membershipRows, error: membershipError } = await client
      .from('conversation_members')
      .select('conversation_id')
      .eq('user_id', user.id);

    if (membershipError) {
      setLoadingConversations(false);
      return;
    }

    const conversationIds = [...new Set((membershipRows ?? []).map((row) => row.conversation_id))];

    if (conversationIds.length === 0) {
      setConversations([]);
      setSelectedConversationId(null);
      setLoadingConversations(false);
      return;
    }

    const [{ data: conversationRows }, { data: memberRows }] = await Promise.all([
      client.from('conversations').select('*').in('id', conversationIds).order('last_message_at', {
        ascending: false,
        nullsFirst: false,
      }),
      client.from('conversation_members').select('*').in('conversation_id', conversationIds),
    ]);

    const members = (memberRows ?? []) as ConversationMember[];
    const memberIds = [...new Set(members.map((member) => member.user_id))];
    const { data: profileRows } = await client.from('profiles').select('*').in('id', memberIds);
    const profileMap = new Map<string, Profile>();
    ((profileRows ?? []) as Profile[]).forEach((memberProfile) => profileMap.set(memberProfile.id, memberProfile));

    const nextConversations = ((conversationRows ?? []) as Conversation[]).map<ConversationSummary>((conversation) => {
      const conversationMembers: ConversationMemberWithProfile[] = members
        .filter((member) => member.conversation_id === conversation.id)
        .map((member) => ({
          ...member,
          profile: profileMap.get(member.user_id) || {
            id: member.user_id,
            username: 'unknown',
            display_name: 'Unknown user',
            bio: null,
            avatar_url: null,
            status: 'offline',
            last_seen: null,
            role: 'user',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        }));

      return { ...conversation, members: conversationMembers };
    });

    setConversations(nextConversations);
    if (!selectedConversationId && nextConversations[0]) {
      setSelectedConversationId(nextConversations[0].id);
    }
    setLoadingConversations(false);
  }, [selectedConversationId, user]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    window.localStorage.setItem(preferencesStorageKey, JSON.stringify(preferences));
    document.documentElement.dataset.theme = preferences.theme;
    document.documentElement.dataset.density = preferences.compactMode ? 'compact' : 'comfortable';
    document.documentElement.dataset.glass = preferences.glassPanels ? 'on' : 'off';
    document.documentElement.dataset.motion = preferences.reduceMotion ? 'reduced' : 'full';
  }, [preferences]);

  useEffect(() => {
    if (!user) return;

    const client = getSupabase();
    const channel = client
      .channel(`conversation-refresh:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversation_members',
          filter: `user_id=eq.${user.id}`,
        },
        () => loadConversations(),
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations',
        },
        () => loadConversations(),
      )
      .subscribe();

    return () => {
      client.removeChannel(channel);
    };
  }, [loadConversations, user]);

  if (!user || !profile) return null;
  const currentProfile = profile;

  function openConversation(conversationId: string) {
    setSelectedConversationId(conversationId);
    setActivePanel('chat');
    loadConversations();
  }

  async function handleRequestNotifications() {
    const nextPermission = await requestPermission();
    if (nextPermission === 'granted') {
      showBrowserNotification('Relay notifications enabled', 'You will get alerts when messages arrive in the background.');
    }
  }

  async function startDirectMessageFromProfile(nextProfile: Profile) {
    if (nextProfile.id === currentProfile.id) {
      setSelectedProfile(null);
      setSettingsOpen(true);
      return;
    }

    const client = getSupabase();
    const { data, error } = await client.rpc('create_direct_conversation', { other_user_id: nextProfile.id });

    if (!error && data) {
      setSelectedProfile(null);
      openConversation(data);
    }
  }

  return (
    <div className="flex h-screen overflow-hidden bg-[var(--relay-app)] text-slate-100">
      <SidebarRail
        activePanel={activePanel}
        onPanelChange={setActivePanel}
        profile={profile}
        onSignOut={signOut}
        onOpenSettings={() => setSettingsOpen(true)}
      />

      {activePanel === 'chat' && (
        <section
          className={`${selectedConversation ? 'hidden md:block' : 'block'} h-screen w-[calc(100vw-64px)] shrink-0 md:w-[300px] lg:w-[336px]`}
        >
          <ConversationList
            conversations={conversations}
            selectedConversationId={selectedConversationId}
            currentUserId={profile.id}
            onlineIds={onlineIds}
            loading={loadingConversations}
            onSelect={(conversationId) => {
              setSelectedConversationId(conversationId);
              setActivePanel('chat');
            }}
            onNewGroup={() => setGroupModalOpen(true)}
          />
        </section>
      )}

      {activePanel === 'friends' ? (
        <FriendRequests currentProfile={profile} onlineIds={onlineIds} onConversationCreated={openConversation} />
      ) : activePanel === 'admin' ? (
        <AdminDashboard currentProfile={profile} />
      ) : (
        <ChatWindow
          conversation={selectedConversation}
          currentProfile={profile}
          onlineIds={onlineIds}
          onBackToInbox={() => setSelectedConversationId(null)}
          onConversationChanged={loadConversations}
          onOpenSettings={() => setSettingsOpen(true)}
          onOpenFriends={() => setActivePanel('friends')}
          notificationPermission={notificationPermission}
          onRequestNotifications={handleRequestNotifications}
          onProfileClick={setSelectedProfile}
        />
      )}

      <ProfilePanel
        profile={profile}
        conversation={selectedConversation}
        onlineIds={onlineIds}
        onOpenSettings={() => setSettingsOpen(true)}
        onProfileClick={setSelectedProfile}
        onConversationChanged={loadConversations}
      />

      {groupModalOpen && (
        <GroupChatModal
          currentProfile={profile}
          onClose={() => setGroupModalOpen(false)}
          onCreated={(conversationId) => {
            setGroupModalOpen(false);
            openConversation(conversationId);
          }}
        />
      )}

      {settingsOpen && (
        <SettingsModal
          profile={profile}
          preferences={preferences}
          notificationPermission={notificationPermission}
          onClose={() => setSettingsOpen(false)}
          onPreferencesChange={setPreferences}
          onUpdateProfile={updateProfile}
          onRequestNotifications={handleRequestNotifications}
          onSignOut={signOut}
        />
      )}

      {selectedProfile && (
        <UserProfileModal
          profile={selectedProfile}
          online={onlineIds.has(selectedProfile.id)}
          onClose={() => setSelectedProfile(null)}
          onMessage={startDirectMessageFromProfile}
        />
      )}
    </div>
  );
}
